import React, { useEffect, useState } from 'react';
import {
    Show, 
    TextField,
    DateField,
    TopToolbar,
    EditButton,
    useRecordContext,
    useDataProvider,
    Loading,
    TabbedShowLayout,
    Tab
} from 'react-admin';
import { Box, Chip, Typography, CircularProgress } from '@mui/material';
import RoleTitle from './fields/roleTitle.component';
import { DATE_TIME_OPTIONS } from '../../app/app.component';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import { rolesFields } from '../roles.model';
import { SectionedShowLayout, LabeledField } from '../../components/layouts';

const PostShowActions = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <TopToolbar>
            <EditButton disabled={record.is_protected} />
        </TopToolbar>
    );
};

const RolePermissionsDisplay = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!record?.id) return;

        const fetchPermissions = async () => {
            try {
                setLoading(true);
                const response = await dataProvider.getList('roles', {
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'id', order: 'ASC' },
                    filter: { id: record.id },
                    meta: { subResource: 'permissions' },
                });

                setPermissions(response.data || []);
            } catch (error) {
                console.error('Failed to fetch role permissions:', error);
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [record?.id, dataProvider]);

    if (loading) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading permissions...</Typography>
            </Box>
        );
    }

    if (permissions.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No permissions assigned to this role
            </Typography>
        );
    }

    // Group permissions by category
    const groupedPermissions = permissions.reduce((acc, perm) => {
        const category = perm.category || 'other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(perm);
        return acc;
    }, {});

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Permissions: {permissions.length}
            </Typography>
            {Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)).map(([category, perms]) => (
                <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize', fontWeight: 600 }}>
                        {category} ({perms.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {perms.sort((a, b) => a.name.localeCompare(b.name)).map(perm => (
                            <Chip
                                key={perm.id}
                                label={perm.name}
                                size="small"
                                sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const RoleShow = () => {
    return (
        <Show actions={<PostShowActions />} title={<RoleTitle />}>
            <TabbedShowLayout>
                <Tab label="Details" path="details">
                    <TextField source={rolesFields.roleId} label="Id" />
                    <TextField source={rolesFields.roleName} label="Name" />
                    <TextField source={rolesFields.roleKey} label="Role Key" emptyText="(auto-generated)" />
                    <TextField source={rolesFields.roleType} label="Role Type" emptyText="legacy" />
                    <TextField source={rolesFields.isProtected} label="Is Protected" />
                    <TextField source={rolesFields.lineType} label="Line Type" emptyText="Not set" />
                    <TextField source={rolesFields.phoneNumber} label="Phone Number" emptyText="Not set" />
                    <DateField source={rolesFields.roleCreatedAt} label="Created At" options={DATE_TIME_OPTIONS} />
                    <DateField source={rolesFields.roleUpdatedAt} label="Updated At" options={DATE_TIME_OPTIONS} />
                </Tab>
                <Tab label="Permissions" path="permissions">
                    <RolePermissionsDisplay />
                </Tab>
            </TabbedShowLayout>
        </Show>
    );
};

export default RoleShow;
